/**
 * Remotion Text Overlay Component
 *
 * Renders TextOverlay from Post.ts with pixel-identical output to Fabric.js.
 * Supports both useSvgFilter (per-line backgrounds) and regular (block background) modes.
 */

import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { continueRender, delayRender } from 'remotion';
import {
  TextOverlay,
  TextOverlayStyle,
  TextRun,
  getRenderableRuns,
  splitRunsIntoParagraphs,
} from '../../models/Post';

interface RemotionTextOverlayProps {
  overlay: TextOverlay;
  containerWidth: number;
  containerHeight: number;
  /** Scale factor: containerWidth / referenceMediaWidth */
  scaleFactor?: number;
  /** Layout engine: 'fabric' (old posts) applies CSS compensations, 'remotion' (new posts) uses clean CSS */
  layoutEngine?: 'fabric' | 'remotion';
  /**
   * Static markup for layout measurement only (renderToStaticMarkup): skips
   * the highlight-pill measurement, which needs a committed DOM. Pills never
   * affect layout, so the measured markup is still exact.
   */
  measureOnly?: boolean;
}

// Default padding values (matching Fabric.js implementation)
const DEFAULT_VERTICAL_PADDING = 8;
const DEFAULT_HORIZONTAL_PADDING = 12;

// Per-line mode (useSvgFilter) padding
const SVG_FILTER_HORIZ_PADDING = 8;
const SVG_FILTER_VERT_PADDING = 4;
const SVG_FILTER_BORDER_RADIUS = 4;

// Inline highlight pill (TextRunStyle.highlightColor). Pills are not CSS
// backgrounds: a highlighted run renders as plain text and HighlightPills
// draws one rounded box per line fragment behind it, sized from the glyph
// ink (cap height / descender / space width measured on a canvas) instead of
// the font's content area. The content area (ascender+descender metrics) is
// taller than the ink on most faces — Poppins' is 1.4em — so a background
// pill at line-height 1.1 overlapped the previous line's descenders and the
// next line's ascenders, and the 0.25em CSS bleed overshot into neighbouring
// words on fonts with narrow spaces. Em-based so everything scales with the
// run's own font size.
/** Vertical pad above the cap height / below the descender ink, shrunk when
 *  the line pitch leaves no room: pills on consecutive lines keep at least
 *  HIGHLIGHT_LINE_GAP_EM between them and never reach the adjacent line's
 *  ink. Where the ink itself interleaves (line-height below ~1.0 with
 *  mixed case) the pad is 0 and the pill is exactly the ink band. */
const HIGHLIGHT_PADDING_VERT_EM = 0.08;
const HIGHLIGHT_LINE_GAP_EM = 0.04;
/** Horizontal bleed past the glyphs. Capped so that HIGHLIGHT_SPACE_CLEARANCE_EM
 *  of the font's space is always left between the pill and the neighbouring
 *  word's advance box (League Gothic / Bebas Neue spaces are ~0.155em). */
const HIGHLIGHT_BLEED_EM = 0.18;
const HIGHLIGHT_SPACE_CLEARANCE_EM = 0.06;
const HIGHLIGHT_BORDER_RADIUS_EM = 0.15;

/**
 * Build the inline style for one styled run. Inherited text properties
 * (color, stroke, shadow, whiteSpace, wordBreak) cascade from the parent
 * span, so only the run's explicit overrides are set here. The highlight
 * pill is NOT part of this style — the span stays width-neutral (no padding
 * or margin) and HighlightPills paints the pill separately.
 */
function runSpanStyle(
  run: TextRun,
  baseStyle: TextOverlayStyle,
  scaleFactor: number
): React.CSSProperties | undefined {
  const s = run.style;
  if (!s) return undefined;
  const style: React.CSSProperties = {};
  if (s.fontFamily) style.fontFamily = s.fontFamily;
  if (s.fontSize) style.fontSize = s.fontSize * scaleFactor;
  if (s.color) style.color = s.color;
  if (s.fontWeight) style.fontWeight = s.fontWeight;
  if (s.fontStyle) style.fontStyle = s.fontStyle;
  if (s.textDecoration) style.textDecoration = s.textDecoration;
  if (s.textTransform) style.textTransform = s.textTransform;
  if (s.strokeWidthRatio !== undefined) {
    const runFontSize = (s.fontSize ?? baseStyle.fontSize) * scaleFactor;
    const width = s.strokeWidthRatio * runFontSize;
    const strokeColor = s.strokeColor ?? baseStyle.strokeColor ?? '#000000';
    (style as Record<string, unknown>).WebkitTextStroke =
      width > 0 ? `${width}px ${strokeColor}` : '0px transparent';
    if (width > 0) (style as Record<string, unknown>).paintOrder = 'stroke fill';
  }
  if (s.textShadow !== undefined) {
    // "none" must be set explicitly to block the inherited base shadow
    style.textShadow =
      s.textShadow === 'none' ? 'none' : parseTextShadow(s.textShadow, scaleFactor);
  }
  return Object.keys(style).length > 0 ? style : undefined;
}

// Text sits in a position:relative inner span so it paints in the positioned
// phase. Highlight pills are absolutely positioned divs with a negative
// z-index inside the overlay root's stacking context (the root is always
// positioned with a numeric z-index), so they paint above the root's block
// background and below everything else in the overlay, whatever the DOM order.
const TEXT_ABOVE_PILLS: React.CSSProperties = { position: 'relative' };

/** A run span registered for pill measurement, keyed by paragraph/run index. */
interface HighlightSpan {
  run: TextRun;
  /** The inner (text) span; its first child is the run's text node. */
  el: HTMLElement;
}
type HighlightRegistry = Map<string, HighlightSpan>;
type RegisterHighlightSpan = (key: string, run: TextRun, el: HTMLElement | null) => void;

/**
 * Renders run texts as inline spans inside a parent text span. Highlighted
 * runs register their text span so HighlightPills can measure them;
 * allowHighlight is off in per-line background mode when the line pills are
 * visible — an inline pill inside a line pill has no sane rendering.
 */
const RunsText: React.FC<{
  runs: TextRun[];
  baseStyle: TextOverlayStyle;
  scaleFactor: number;
  allowHighlight: boolean;
  register?: RegisterHighlightSpan;
  keyPrefix?: string;
}> = ({ runs, baseStyle, scaleFactor, allowHighlight, register, keyPrefix = '' }) => (
  <>
    {runs.map((run, i) => {
      const style = runSpanStyle(run, baseStyle, scaleFactor);
      const highlighted = allowHighlight && !!run.style?.highlightColor && !!register;
      const ref = highlighted
        ? (el: HTMLElement | null) => register!(`${keyPrefix}${i}`, run, el)
        : undefined;
      return style ? (
        <span key={i} style={style}>
          <span style={TEXT_ABOVE_PILLS} ref={ref}>{run.text}</span>
        </span>
      ) : (
        <span key={i} style={TEXT_ABOVE_PILLS} ref={ref}>{run.text}</span>
      );
    })}
  </>
);

/** One pill box, in the overlay root's un-rotated, unscaled px space. */
interface PillBox {
  left: number;
  top: number;
  width: number;
  height: number;
  color: string;
  borderRadius: number;
}

interface InkMetrics {
  /** Font ascent / descent (content area) in px, for locating the baseline */
  fontAscent: number;
  fontDescent: number;
  /** Ink above the baseline that the pill must cover, in em */
  topEm: number;
  /** Ink below the baseline that the pill must cover, in em */
  bottomEm: number;
  /** The font's ascender / descender ink extents in em — what any
   *  neighbouring line of the same font can reach, whatever its text */
  fontTopEm: number;
  fontBottomEm: number;
  /** Width of a space, in em */
  spaceEm: number;
}

let inkCanvas: CanvasRenderingContext2D | null | undefined;
function getInkContext(): CanvasRenderingContext2D | null {
  if (inkCanvas === undefined) {
    try {
      inkCanvas = document.createElement('canvas').getContext('2d');
    } catch {
      inkCanvas = null;
    }
  }
  return inkCanvas;
}

function applyTextTransform(text: string, transform: string): string {
  switch (transform) {
    case 'uppercase':
      return text.toUpperCase();
    case 'lowercase':
      return text.toLowerCase();
    case 'capitalize':
      return text.replace(/(^|\s)(\S)/g, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
    default:
      return text;
  }
}

/**
 * Glyph ink metrics for a run's resolved font, from a canvas. The top edge is
 * the cap height ("H") or the text's own ink ascent if taller (accents,
 * ascenders); the bottom edge is the text's own ink descent, so an all-caps or
 * descender-free run gets a pill that hugs the baseline. The font extents
 * probe ascender and descender letters (through the run's text-transform, so
 * an uppercase overlay has no descenders). Falls back to typical proportions
 * when the canvas or its metrics are unavailable.
 */
function measureInk(el: HTMLElement, text: string): InkMetrics {
  const cs = getComputedStyle(el);
  const fontSize = parseFloat(cs.fontSize) || 1;
  const fallback: InkMetrics = {
    fontAscent: 0.8 * fontSize,
    fontDescent: 0.2 * fontSize,
    topEm: 0.72,
    bottomEm: 0.22,
    fontTopEm: 0.76,
    fontBottomEm: 0.22,
    spaceEm: 0.25,
  };
  const ctx = getInkContext();
  if (!ctx) return fallback;
  try {
    ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    const rendered = applyTextTransform(text, cs.textTransform);
    const ink = ctx.measureText(rendered);
    const cap = ctx.measureText('H');
    const ascenders = ctx.measureText(applyTextTransform('Hbdfhklt', cs.textTransform));
    const descenders = ctx.measureText(applyTextTransform('gjpqy', cs.textTransform));
    const space = ctx.measureText(' ');
    if (!Number.isFinite(ink.fontBoundingBoxAscent) || !Number.isFinite(cap.actualBoundingBoxAscent)) {
      return fallback;
    }
    const topEm = Math.max(cap.actualBoundingBoxAscent, ink.actualBoundingBoxAscent) / fontSize;
    const bottomEm = Math.max(0, ink.actualBoundingBoxDescent) / fontSize;
    return {
      fontAscent: ink.fontBoundingBoxAscent,
      fontDescent: ink.fontBoundingBoxDescent,
      topEm,
      bottomEm,
      fontTopEm: Math.max(topEm, ascenders.actualBoundingBoxAscent / fontSize),
      fontBottomEm: Math.max(bottomEm, descenders.actualBoundingBoxDescent / fontSize),
      spaceEm: space.width / fontSize,
    };
  } catch {
    return fallback;
  }
}

/**
 * Temporarily remove every CSS transform between `root` and the document
 * body (inclusive of root) so client rects are read in untransformed layout
 * space. Nothing paints in between: this runs inside a layout effect. The
 * root's own rotation, the editor Player's scale and a video entrance
 * animation (which can sit at scale(0) on the frame the overlay mounts) all
 * live on these transforms. Transitions are suppressed while toggling and
 * the restored transform is flushed before they come back, so an ancestor
 * with a transform transition does not animate.
 */
function withTransformsNeutralised<T>(root: HTMLElement, read: () => T): T {
  const touched: { el: HTMLElement; transform: string; translate: string; scale: string; rotate: string; transition: string }[] = [];
  for (let el: HTMLElement | null = root; el && el !== document.body; el = el.parentElement) {
    const cs = getComputedStyle(el);
    const individual = (cs as unknown as Record<string, string>);
    const transformed =
      el === root ||
      cs.transform !== 'none' ||
      (individual.translate && individual.translate !== 'none') ||
      (individual.scale && individual.scale !== 'none') ||
      (individual.rotate && individual.rotate !== 'none');
    if (!transformed) continue;
    const st = el.style as CSSStyleDeclaration & Record<string, string>;
    touched.push({ el, transform: st.transform, translate: st.translate, scale: st.scale, rotate: st.rotate, transition: st.transition });
    st.transition = 'none';
    st.transform = 'none';
    st.translate = 'none';
    st.scale = 'none';
    st.rotate = 'none';
  }
  try {
    return read();
  } finally {
    for (const t of touched) {
      const st = t.el.style as CSSStyleDeclaration & Record<string, string>;
      st.transform = t.transform;
      st.translate = t.translate;
      st.scale = t.scale;
      st.rotate = t.rotate;
    }
    if (touched.length > 0) {
      // Flush the restored transforms while transitions are still off
      void touched[0].el.offsetWidth;
      for (const t of touched) t.el.style.transition = t.transition;
    }
  }
}

/**
 * Measure every registered highlighted run against the committed layout and
 * compute its pill boxes, one per line fragment. Rects are read with every
 * transform up to the body neutralised (see withTransformsNeutralised), so
 * the boxes are in the root's own px space and rotate/scale/animate with it.
 */
function measurePills(
  root: HTMLElement,
  spans: HighlightRegistry,
  scaleFactor: number,
  rootWidthPx: number | undefined,
  lineHeightPx: number
): PillBox[] {
  if (typeof document === 'undefined' || typeof document.createRange !== 'function') return [];
  const entries = Array.from(spans.values()).filter((s) => s.el.isConnected);
  if (entries.length === 0) return [];

  return withTransformsNeutralised(root, () => {
    const rootRect = root.getBoundingClientRect();
    // Residual scale guard (e.g. CSS zoom on an ancestor); transforms are off
    const unscaledWidth = rootWidthPx ?? root.offsetWidth;
    const scale = unscaledWidth > 0 && rootRect.width > 0 ? rootRect.width / unscaledWidth : 1;

    // Per-run ink metrics; the vertical extents are shared across the overlay
    // (in em) so every pill in it has the same proportions.
    const measured = entries.map((entry) => {
      const text = entry.el.textContent ?? '';
      return { entry, text, ink: measureInk(entry.el, text) };
    });
    const topEm = Math.max(...measured.map((m) => m.ink.topEm));
    const bottomEm = Math.max(...measured.map((m) => m.ink.bottomEm));

    const pills: PillBox[] = [];
    const range = document.createRange();
    for (const { entry, text, ink } of measured) {
      const style = entry.run.style!;
      const textNode = entry.el.firstChild;
      if (!textNode || textNode.nodeType !== Node.TEXT_NODE || typeof range.getClientRects !== 'function') continue;
      const fontSize = parseFloat(getComputedStyle(entry.el).fontSize) || 1;
      // Room between what this font's ink can reach on this line and on the
      // neighbouring lines (their text is not the pill's, so the font's full
      // ascender/descender extents apply, not this run's)
      const slackEm = lineHeightPx / fontSize - ink.fontTopEm - ink.fontBottomEm;
      const padV =
        Math.max(0, Math.min(HIGHLIGHT_PADDING_VERT_EM, (slackEm - HIGHLIGHT_LINE_GAP_EM) / 2)) * fontSize;
      const bleed =
        Math.max(0, Math.min(HIGHLIGHT_BLEED_EM, ink.spaceEm - HIGHLIGHT_SPACE_CLEARANCE_EM)) * fontSize;
      const color = hexToRgba(style.highlightColor!, (style.opacity ?? 100) / 100);
      const borderRadius =
        style.borderRadius !== undefined
          ? style.borderRadius * scaleFactor
          : HIGHLIGHT_BORDER_RADIUS_EM * fontSize;
      const baselineRatio =
        ink.fontAscent + ink.fontDescent > 0 ? ink.fontAscent / (ink.fontAscent + ink.fontDescent) : 0.8;

      // Word rects (whitespace excluded, so a pill never covers a line's
      // leading or trailing spaces), grouped per line fragment.
      const lines: { top: number; left: number; right: number; baseline: number }[] = [];
      const wordPattern = /\S+/g;
      let match: RegExpExecArray | null;
      while ((match = wordPattern.exec(text)) !== null) {
        range.setStart(textNode, match.index);
        range.setEnd(textNode, match.index + match[0].length);
        for (const rect of Array.from(range.getClientRects())) {
          if (rect.width <= 0 || rect.height <= 0) continue;
          const baseline = rect.top + rect.height * baselineRatio;
          const line = lines.find((l) => Math.abs(l.top - rect.top) < 0.5);
          if (line) {
            line.left = Math.min(line.left, rect.left);
            line.right = Math.max(line.right, rect.right);
          } else {
            lines.push({ top: rect.top, left: rect.left, right: rect.right, baseline });
          }
        }
      }

      for (const line of lines) {
        const left = (line.left - rootRect.left) / scale - bleed;
        const right = (line.right - rootRect.left) / scale + bleed;
        const baseline = (line.baseline - rootRect.top) / scale;
        const top = baseline - topEm * fontSize - padV;
        const bottom = baseline + bottomEm * fontSize + padV;
        pills.push({ left, top, width: right - left, height: bottom - top, color, borderRadius });
      }
    }
    return pills;
  });
}

function pillsEqual(a: PillBox[], b: PillBox[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((p, i) => {
    const q = b[i];
    return (
      Math.abs(p.left - q.left) < 0.01 &&
      Math.abs(p.top - q.top) < 0.01 &&
      Math.abs(p.width - q.width) < 0.01 &&
      Math.abs(p.height - q.height) < 0.01 &&
      p.color === q.color &&
      p.borderRadius === q.borderRadius
    );
  });
}

/**
 * Paints the highlight pills of an overlay as absolutely positioned boxes in
 * its root (its parent element, found through a display:none anchor whose
 * ref is attached before this component's layout effect runs — the root's
 * own ref would not be yet), behind the text. Must be the root's LAST child:
 * React attaches refs and runs layout effects in tree order, so only then
 * have all run spans registered when the effect measures. Measures after
 * each commit, again once fonts settle (the editor loads fonts after
 * mounting; the export waits on a delayRender handle so the still includes
 * the pills), and whenever the document's font set finishes loading.
 */
const HighlightPills: React.FC<{
  spans: HighlightRegistry;
  scaleFactor: number;
  rootWidthPx: number | undefined;
  /** The overlay's line pitch in px (line-height × base font size) */
  lineHeightPx: number;
  /** Change key: anything that can move the text */
  layoutKey: unknown;
}> = ({ spans, scaleFactor, rootWidthPx, lineHeightPx, layoutKey }) => {
  const [pills, setPills] = useState<PillBox[]>([]);
  const pillsRef = useRef(pills);
  pillsRef.current = pills;
  const anchorRef = useRef<HTMLSpanElement | null>(null);

  const measure = useCallback(() => {
    const root = anchorRef.current?.parentElement;
    if (!root) return;
    let next: PillBox[];
    try {
      next = measurePills(root, spans, scaleFactor, rootWidthPx, lineHeightPx);
    } catch (e) {
      console.warn('[RemotionTextOverlay] Highlight pill measurement failed', e);
      next = [];
    }
    if (!pillsEqual(pillsRef.current, next)) {
      pillsRef.current = next;
      setPills(next);
    }
  }, [spans, scaleFactor, rootWidthPx, lineHeightPx]);

  useLayoutEffect(() => {
    measure();
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
    if (!fonts) return;
    let disposed = false;
    const handle = delayRender('Measuring highlight pills');
    let continued = false;
    const finish = () => {
      if (!continued) {
        continued = true;
        continueRender(handle);
      }
    };
    fonts.ready.then(
      () => {
        if (!disposed) measure();
        finish();
      },
      finish
    );
    const onLoadingDone = () => {
      if (!disposed) measure();
    };
    fonts.addEventListener('loadingdone', onLoadingDone);
    return () => {
      disposed = true;
      fonts.removeEventListener('loadingdone', onLoadingDone);
      finish();
    };
    // layoutKey stands in for every prop that can move the text
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, layoutKey]);

  return (
    <>
      <span ref={anchorRef} style={{ display: 'none' }} />
      {pills.map((pill, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: pill.left,
            top: pill.top,
            width: pill.width,
            height: pill.height,
            backgroundColor: pill.color,
            borderRadius: pill.borderRadius,
            zIndex: -1,
            pointerEvents: 'none',
          }}
        />
      ))}
    </>
  );
};

/** Registry of highlighted run spans for one overlay; stable across renders. */
function useHighlightRegistry(): [HighlightRegistry, RegisterHighlightSpan] {
  const [spans] = useState<HighlightRegistry>(() => new Map());
  const register = useCallback<RegisterHighlightSpan>(
    (key, run, el) => {
      if (el) spans.set(key, { run, el });
      else spans.delete(key);
    },
    [spans]
  );
  return [spans, register];
}

/**
 * Parse and scale text shadow CSS string
 */
function parseTextShadow(shadow: string | undefined, scaleFactor: number = 1): string | undefined {
  if (!shadow || shadow === 'none') return undefined;

  // Parse shadow: "2px 2px 4px rgba(0,0,0,0.5)" or "2px 2px 4px #000"
  // Format: offsetX offsetY blur color
  const match = shadow.match(/^(-?\d+(?:\.\d+)?)(px)?\s+(-?\d+(?:\.\d+)?)(px)?\s+(\d+(?:\.\d+)?)(px)?\s+(.+)$/);
  if (!match) return shadow; // Return as-is if can't parse

  const offsetX = parseFloat(match[1]) * scaleFactor;
  const offsetY = parseFloat(match[3]) * scaleFactor;
  const blur = parseFloat(match[5]) * scaleFactor;
  const color = match[7].trim();

  return `${offsetX}px ${offsetY}px ${blur}px ${color}`;
}

/**
 * Convert hex color + opacity to rgba
 */
function hexToRgba(hex: string, opacity: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Check if text contains CJK characters (for word-break handling)
 */
function containsCJK(text: string): boolean {
  return /[\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\u3040-\u309f\u30a0-\u30ff\uac00-\ud7af]/.test(text);
}

export const RemotionTextOverlay: React.FC<RemotionTextOverlayProps> = ({
  overlay,
  containerWidth,
  containerHeight,
  scaleFactor = 1,
  layoutEngine,
  measureOnly = false,
}) => {
  const { text, style, position, width, height, rotation = 0, zIndex = 0 } = overlay;

  // Styled runs (mixed styles within the text); null falls back to uniform style
  const renderableRuns = useMemo(() => getRenderableRuns(overlay), [overlay]);
  const hasHighlights = !!renderableRuns?.some((r) => r.style?.highlightColor);
  const [highlightSpans, registerHighlightSpan] = useHighlightRegistry();
  // Pills are re-measured whenever anything that can move the text changes
  const pillLayoutKey = useMemo(
    () => [overlay, containerWidth, containerHeight, scaleFactor, layoutEngine],
    [overlay, containerWidth, containerHeight, scaleFactor, layoutEngine]
  );

  // Sanitize style with defaults
  const safeStyle: TextOverlayStyle = useMemo(() => ({
    ...style,
    fontFamily: style.fontFamily || 'Arial',
    fontWeight: style.fontWeight || 'normal',
    fontStyle: style.fontStyle || 'normal',
    textAlign: style.textAlign || 'left',
    lineHeight: style.lineHeight ?? 1.4,
    textDecoration: style.textDecoration || 'none',
  }), [style]);

  // Calculate dimensions
  const left = (position.x / 100) * containerWidth;
  const topRaw = (position.y / 100) * containerHeight;
  const widthPx = width ? (width / 100) * containerWidth : undefined;
  const heightPx = height ? (height / 100) * containerHeight : undefined;

  // Apply scale factor to font size
  const scaledFontSize = safeStyle.fontSize * scaleFactor;

  // FABRIC.JS COMPATIBILITY COMPENSATION
  // =====================================
  // CSS line-height adds "half-leading" above the first line, pushing text down.
  // Fabric.js (canvas) doesn't do this - text starts at the top of the bounding box.
  //
  // For line-height 1.4: half-leading = (1.4 - 1.0) / 2 = 0.2 * fontSize above the text
  //
  // This compensation is ONLY applied for old posts (layoutEngine !== 'remotion').
  // New posts created with Remotion use clean CSS positioning without compensation.
  //
  // See MediaItem.metadata.layoutEngine in Post.ts for migration strategy.
  const lineHeight = safeStyle.lineHeight ?? 1.4;
  const useFabricCompensation = layoutEngine !== 'remotion';
  const halfLeading = useFabricCompensation ? ((lineHeight - 1.0) / 2) * scaledFontSize : 0;
  const top = topRaw - halfLeading;

  // Background color with opacity
  const hasBackground = !!(safeStyle.backgroundColor && safeStyle.opacity > 0);
  const backgroundColor = hasBackground
    ? hexToRgba(safeStyle.backgroundColor, safeStyle.opacity / 100)
    : 'transparent';

  // Stroke (text outline) - scale the stroke width
  const strokeWidth = safeStyle.strokeWidthRatio
    ? safeStyle.strokeWidthRatio * scaledFontSize
    : 0;
  const hasStroke = strokeWidth > 0 && safeStyle.strokeColor;

  // Text shadow - scale shadow offsets and blur
  const textShadow = parseTextShadow(safeStyle.textShadow, scaleFactor);

  // CJK word break
  const wordBreak = containsCJK(text) ? 'break-all' : 'normal';

  // Render based on mode
  if (safeStyle.useSvgFilter) {
    return (
      <PerLineBackgroundText
        text={text}
        runs={renderableRuns}
        style={safeStyle}
        left={left}
        top={top}
        widthPx={widthPx}
        rotation={rotation}
        zIndex={zIndex}
        hasStroke={!!hasStroke}
        strokeWidth={strokeWidth}
        textShadow={textShadow}
        wordBreak={wordBreak}
        backgroundColor={backgroundColor}
        hasBackground={hasBackground}
        scaleFactor={scaleFactor}
        highlightSpans={highlightSpans}
        registerHighlightSpan={registerHighlightSpan}
        showPills={hasHighlights && !measureOnly}
        pillLayoutKey={pillLayoutKey}
      />
    );
  }

  // Regular block background mode - scale padding and border radius
  const padding = safeStyle.padding;
  const paddingTop = (padding?.top ?? DEFAULT_VERTICAL_PADDING) * scaleFactor;
  const paddingRight = (padding?.right ?? DEFAULT_HORIZONTAL_PADDING) * scaleFactor;
  const paddingBottom = (padding?.bottom ?? DEFAULT_VERTICAL_PADDING) * scaleFactor;
  const paddingLeft = (padding?.left ?? DEFAULT_HORIZONTAL_PADDING) * scaleFactor;
  const borderRadius = (safeStyle.borderRadius ?? 0) * scaleFactor;

  return (
    <div
      className="notranslate"
      translate="no"
      style={{
        position: 'absolute',
        left,
        top,
        width: widthPx,
        height: heightPx,
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
        transformOrigin: 'center',
        zIndex,
        // Background
        backgroundColor: hasBackground ? backgroundColor : 'transparent',
        borderRadius,
        padding: hasBackground ? `${paddingTop}px ${paddingRight}px ${paddingBottom}px ${paddingLeft}px` : 0,
        boxSizing: 'border-box',
      }}
    >
      <span
        style={{
          // Text styling
          fontFamily: safeStyle.fontFamily,
          fontSize: scaledFontSize,
          fontWeight: safeStyle.fontWeight,
          fontStyle: safeStyle.fontStyle,
          color: safeStyle.color,
          textAlign: safeStyle.textAlign,
          textDecoration: safeStyle.textDecoration,
          textTransform: safeStyle.textTransform,
          lineHeight: safeStyle.lineHeight,
          // Stroke effect using text-stroke
          WebkitTextStroke: hasStroke ? `${strokeWidth}px ${safeStyle.strokeColor}` : undefined,
          paintOrder: hasStroke ? 'stroke fill' : undefined,
          // Shadow
          textShadow,
          // Word wrapping
          display: 'block',
          whiteSpace: 'pre-wrap',
          wordBreak,
          overflowWrap: 'normal',
          hyphens: 'none',
        }}
      >
        {renderableRuns ? (
          <RunsText
            runs={renderableRuns}
            baseStyle={safeStyle}
            scaleFactor={scaleFactor}
            allowHighlight
            register={registerHighlightSpan}
          />
        ) : (
          text
        )}
      </span>
      {hasHighlights && !measureOnly && (
        <HighlightPills
          spans={highlightSpans}
          scaleFactor={scaleFactor}
          rootWidthPx={widthPx}
          lineHeightPx={lineHeight * scaledFontSize}
          layoutKey={pillLayoutKey}
        />
      )}
    </div>
  );
};

/**
 * Per-line background mode (useSvgFilter = true)
 * Each visually wrapped line gets its own background pill using CSS box-decoration-break
 */
interface PerLineBackgroundTextProps {
  text: string;
  runs: TextRun[] | null;
  style: TextOverlayStyle;
  left: number;
  top: number;
  widthPx: number | undefined;
  rotation: number;
  zIndex: number;
  hasStroke: boolean;
  strokeWidth: number;
  textShadow: string | undefined;
  wordBreak: 'normal' | 'break-all';
  backgroundColor: string;
  hasBackground: boolean;
  scaleFactor: number;
  highlightSpans: HighlightRegistry;
  registerHighlightSpan: RegisterHighlightSpan;
  showPills: boolean;
  pillLayoutKey: unknown;
}

const PerLineBackgroundText: React.FC<PerLineBackgroundTextProps> = ({
  text,
  runs,
  style,
  left,
  top,
  widthPx,
  rotation,
  zIndex,
  hasStroke,
  strokeWidth,
  textShadow,
  wordBreak,
  backgroundColor,
  hasBackground,
  scaleFactor,
  highlightSpans,
  registerHighlightSpan,
  showPills,
  pillLayoutKey,
}) => {
  // Scale font size and spacing
  const scaledFontSize = style.fontSize * scaleFactor;
  const scaledVertPadding = SVG_FILTER_VERT_PADDING * scaleFactor;
  const scaledHorizPadding = SVG_FILTER_HORIZ_PADDING * scaleFactor;
  const scaledBorderRadius = SVG_FILTER_BORDER_RADIUS * scaleFactor;

  // Split text by double newlines into paragraphs (paragraph breaks)
  // Single \n is handled by white-space: pre-wrap as a normal line break
  const paragraphs = text.split(/\n\n+/);
  const runParagraphs = runs ? splitRunsIntoParagraphs(runs) : null;

  // Inline highlight pills are suppressed while the per-line background is
  // visible: a pill inside a line pill has no sane rendering.
  const allowRunHighlight = !hasBackground;

  // Line height for per-line backgrounds (inside PerLineBackgroundText, so useSvgFilter is true)
  // Default to 1.4 to avoid overlap; respect user value if explicitly set
  const effectiveLineHeight = style.lineHeight ?? 1.4;

  // Gap between paragraphs (double newlines)
  // Should be about one line height
  const paragraphGap = scaledFontSize * effectiveLineHeight;

  const textStyles: React.CSSProperties = {
    // Use inline display so box-decoration-break works on wrapped lines
    display: 'inline',
    fontFamily: style.fontFamily,
    fontSize: scaledFontSize,
    fontWeight: style.fontWeight,
    fontStyle: style.fontStyle,
    color: style.color,
    lineHeight: effectiveLineHeight,
    textDecoration: style.textDecoration,
    textTransform: style.textTransform,
    // Per-line background using box-decoration-break
    backgroundColor: hasBackground ? backgroundColor : 'transparent',
    borderRadius: scaledBorderRadius,
    padding: hasBackground
      ? `${scaledVertPadding}px ${scaledHorizPadding}px`
      : 0,
    // This is the key: clone decoration on each line break
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
    // Stroke
    WebkitTextStroke: hasStroke ? `${strokeWidth}px ${style.strokeColor}` : undefined,
    paintOrder: hasStroke ? 'stroke fill' : undefined,
    // Shadow
    textShadow,
    // Word break
    wordBreak,
    overflowWrap: 'normal',
    hyphens: 'none',
    // Preserve explicit newlines
    whiteSpace: 'pre-wrap',
  };

  return (
    <div
      className="notranslate"
      translate="no"
      style={{
        position: 'absolute',
        left,
        top,
        width: widthPx,
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
        transformOrigin: 'center',
        zIndex,
        textAlign: style.textAlign || 'left',
        lineHeight: effectiveLineHeight,
        fontSize: scaledFontSize,
      }}
    >
      {(runParagraphs ?? paragraphs).map((paragraph, index) => (
        <div
          key={index}
          style={{
            // Add gap between paragraphs (after double newlines)
            marginTop: index > 0 ? paragraphGap : 0,
            lineHeight: effectiveLineHeight,
          }}
        >
          <span style={textStyles}>
            <span style={TEXT_ABOVE_PILLS}>
              {Array.isArray(paragraph) ? (
                <RunsText
                  runs={paragraph}
                  baseStyle={style}
                  scaleFactor={scaleFactor}
                  allowHighlight={allowRunHighlight}
                  register={registerHighlightSpan}
                  keyPrefix={`${index}-`}
                />
              ) : (
                paragraph
              )}
            </span>
          </span>
        </div>
      ))}
      {showPills && allowRunHighlight && (
        <HighlightPills
          spans={highlightSpans}
          scaleFactor={scaleFactor}
          rootWidthPx={widthPx}
          lineHeightPx={effectiveLineHeight * scaledFontSize}
          layoutKey={pillLayoutKey}
        />
      )}
    </div>
  );
};

export default RemotionTextOverlay;
